import $http from '@vue-util/http';

import { errorHandler } from '@/utils/ErrorHandler';
import { orderLookupFailedAttemptsConfig } from '@/utils/OrderLookupFailedAttempts';
import { ORDER_LOOKUP_ENDPOINT, ORDER_DETAILS_TOKEN_ENDPOINT, ORDER_DETAILS_ENDPOINT } from '#shared/endpoints';
import * as config from 'config';

import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
const { orderDetailsFallbackError, giftReturnFallbackError, orderLookupFailedAttemptsMessage } = config.ORDER_DETAILS;

export const useOrderDetailsStore = defineStore('orderDetailsStore', {
  state: () => ({
    order: null,
    itemDetails: {
      itemName: '',
      reservationNumber: '',
      sortIdentifier: '',
      lineNumber: '',
      cancelItemEligibleFlag: false,
      willingToWaitInd: false,
      orderToken: '',
      orderId: '',
    },
    orderDetailsError: '',
    isLoading: false,
    killSwitch: {},
    updateOrderDetailsApiError: null,
  }),
  actions: {
    SET_ONLINE_ORDER_DETAILS(order) {
      this.order = order;
      this.orderDetailsError = '';
    },
    SET_ITEM_DETAILS_OVERLAY(itemDetails) {
      this.itemDetails.itemName = itemDetails.itemName;
      this.itemDetails.reservationNumber = itemDetails.reservationNumber;
      this.itemDetails.sortIdentifier = itemDetails.sortIdentifier;
      this.itemDetails.lineNumber = itemDetails.lineNumber;
      this.itemDetails.cancelItemEligibleFlag = itemDetails.cancelItemEligibleFlag;
      this.itemDetails.willingToWaitInd = itemDetails.willingToWaitInd;
      this.itemDetails.orderToken = itemDetails.orderToken;
      this.itemDetails.orderId = itemDetails.orderId;
      this.orderDetailsError = '';
    },
    SET_ONLINE_ORDER_DETAILS_ERRORS(error) {
      if (!error?.keepState) this.order = null;
      this.orderDetailsError = error?.msg || error;
    },
    SET_ONLINE_ORDER_DETAILS_LOADER(isLoading) {
      this.isLoading = isLoading;
    },
    SET_KILL_SWITCH(killSwitch) {
      this.killSwitch = killSwitch;
    },
    SET_UPDATE_ORDER_DETAILS_API_ERROR(payload) {
      this.updateOrderDetailsApiError = payload;
    },

    async FETCH_FROM_LOOKUP({ payload, router, headers }) {
      try {
        if (orderLookupFailedAttemptsConfig.isOrderLookupFailedAttempts()) {
          throw orderLookupFailedAttemptsMessage;
        }

        const response = await $http.post(`${ORDER_LOOKUP_ENDPOINT}/${payload.orderNumber}`, payload?.payload || {}, {
          headers,
          ...(payload?.onlyOrderNumber && { params: { isOnlyLookupByOrderIdFlag: payload?.onlyOrderNumber } }),
        });
        orderLookupFailedAttemptsConfig.clearOrderLookupAttemptsFailed();
        if (!response.data?.order) {
          throw new Error();
        }
        this.SET_KILL_SWITCH(response.data?.killSwitch);
        this.SET_ONLINE_ORDER_DETAILS(response.data.order);
        const orderToken = response.data.order.shipments[0]?.orderToken;
        const queryString = orderToken ? `?o=${encodeURIComponent(orderToken)}` : '';

        if (payload.fromLookup === 'returns') {
          router.href = `/purchases/online/${response.data.order.orderNumber}${queryString}`;
        } else {
          router.push({
            name: 'Order Details',
            params: {
              orderNumber: response.data.order.orderNumber,
            },
            query: {
              o: orderToken,
            },
          });
        }
      } catch (error) {
        orderLookupFailedAttemptsConfig.setIsOrderLookupAttemptsFailed();
        this.SET_ONLINE_ORDER_DETAILS_ERRORS(errorHandler(error, orderDetailsFallbackError));
        this.SET_UPDATE_ORDER_DETAILS_API_ERROR(error?.response?.data?.errors?.error?.[0]);
      }
    },

    async FETCH_WITH_ORDER_TOKEN({ payload, headers }) {
      try {
        const response = await $http.get(`${ORDER_DETAILS_TOKEN_ENDPOINT}`, {
          headers,
          params: {
            ordertoken: payload.orderToken,
            ...(payload.locksFlag && { locksFlag: payload.locksFlag }),
          },
        });
        if (!response.data?.order) {
          throw new Error();
        }
        this.SET_KILL_SWITCH(response.data?.killSwitch);
        this.SET_ONLINE_ORDER_DETAILS(response.data.order);
      } catch (error) {
        this.SET_ONLINE_ORDER_DETAILS_ERRORS(errorHandler(error, orderDetailsFallbackError));
        this.SET_UPDATE_ORDER_DETAILS_API_ERROR(error?.response?.data?.errors?.error?.[0]);
      }
    },

    async FETCH_WITH_ORDER_DETAIL({ payload, router, headers }) {
      try {
        if (payload.isGiftReturn && orderLookupFailedAttemptsConfig.isOrderLookupFailedAttempts()) {
          throw orderLookupFailedAttemptsMessage;
        }
        const response = await $http.get(`${ORDER_DETAILS_ENDPOINT}/${payload.orderNumber}`, {
          headers,
          ...(payload.locksFlag && { params: { locksFlag: payload.locksFlag } }),
        });
        if (payload.isGiftReturn) {
          orderLookupFailedAttemptsConfig.clearOrderLookupAttemptsFailed();
        }
        if (!response.data?.order) {
          throw new Error();
        }
        this.SET_KILL_SWITCH(response.data?.killSwitch);
        this.SET_ONLINE_ORDER_DETAILS(response.data.order);
        if (payload.isGiftReturn) {
          router.push({
            name: 'Order Details',
            params: {
              orderNumber: response.data.order.orderNumber,
            },
            query: {
              o: response.data.order.shipments[0]?.orderToken,
            },
          });
        }
      } catch (error) {
        const fallbackError = payload.isGiftReturn ? giftReturnFallbackError : orderDetailsFallbackError;
        if (payload.isGiftReturn) orderLookupFailedAttemptsConfig.setIsOrderLookupAttemptsFailed();
        this.SET_ONLINE_ORDER_DETAILS_ERRORS(errorHandler(error, fallbackError));
        this.SET_UPDATE_ORDER_DETAILS_API_ERROR(error?.response?.data?.errors?.error?.[0]);
      }
    },

    async GET_ONLINE_ORDER_DETAILS({ payload, router }) {
      const envPropsStore = useEnvPropsStore();
      const headers = {
        'X-Macys-ClientId': 'SITE',
        'X-Macys-DeviceId': envPropsStore.deviceType,
      };
      this.SET_ONLINE_ORDER_DETAILS_ERRORS('');
      this.SET_ONLINE_ORDER_DETAILS_LOADER(true);
      this.SET_UPDATE_ORDER_DETAILS_API_ERROR(null);

      try {
        if (payload.fromLookup) {
          await this.FETCH_FROM_LOOKUP({ payload, router, headers });
        } else if (payload.orderToken) {
          await this.FETCH_WITH_ORDER_TOKEN({ payload, headers });
        } else if (payload.orderDetail) {
          await this.FETCH_WITH_ORDER_DETAIL({ payload, router, headers });
        }
      } finally {
        this.SET_ONLINE_ORDER_DETAILS_LOADER(false);
      }
      // Ensure store is updated before returning
      await nextTick();
    },

    CLEAR_ORDER_DETAILS() {
      this.SET_ONLINE_ORDER_DETAILS(null);
      this.SET_KILL_SWITCH({});
      this.SET_ITEM_DETAILS_OVERLAY({
        itemName: '',
        reservationNumber: '',
        sortIdentifier: '',
        lineNumber: '',
        cancelItemEligibleFlag: false,
        orderToken: '',
        willingToWaitInd: false,
        orderId: '',
      });
    },
  },
});
